import * as React from 'react';
import {
  Plus,
  Edit,
  Trash2,
  Coins,
  Medal,
  Dumbbell,
  BookOpen,
  Unlock,
  Gift,
  ChartBar,
  FileText,
} from 'lucide-react';
import { LoyaltyRule, LoyaltyIndexPaths } from '../../LoyaltyIndex.type';

interface RulesTableProps {
  rules: LoyaltyRule[];
  paths: LoyaltyIndexPaths;
  accountCode: string;
}

function getRewardIcon(type?: string) {
  switch (type) {
    case 'points':
      return <Coins size={14} />;
    case 'badge':
      return <Medal size={14} />;
    case 'program_access':
      return <Dumbbell size={14} />;
    case 'course_access':
      return <BookOpen size={14} />;
    case 'full_content_access':
      return <Unlock size={14} />;
    default:
      return <Gift size={14} />;
  }
}

function getRewardBadge(reward: LoyaltyRule['reward']) {
  const icon = getRewardIcon(reward.type);

  switch (reward.type) {
    case 'points':
      return (
        <span className="loyalty-badge loyalty-badge--info">
          {icon}
          Pontos ({reward.pointsValue})
        </span>
      );
    case 'badge':
      return (
        <span className="loyalty-badge loyalty-badge--warning">
          {icon}
          Badge
        </span>
      );
    case 'program_access':
      return (
        <span className="loyalty-badge loyalty-badge--success">
          {icon}
          Acesso Programa
        </span>
      );
    case 'course_access':
      return (
        <span className="loyalty-badge loyalty-badge--primary">
          {icon}
          Acesso Curso
        </span>
      );
    case 'full_content_access':
      return (
        <span className="loyalty-badge loyalty-badge--danger">
          {icon}
          Acesso Total
        </span>
      );
    default:
      return (
        <span className="loyalty-badge">
          {icon}
          {reward.type || 'Sem recompensa'}
        </span>
      );
  }
}

function getStatusBadge(status: LoyaltyRule['status']) {
  switch (status) {
    case 'active':
      return <span className="loyalty-badge loyalty-badge--success">Ativo</span>;
    case 'draft':
      return <span className="loyalty-badge">Rascunho</span>;
    case 'inactive':
      return <span className="loyalty-badge loyalty-badge--warning">Inativo</span>;
    default:
      return <span className="loyalty-badge">{status}</span>;
  }
}

export function RulesTable({ rules, paths, accountCode }: RulesTableProps) {
  const activeRules = rules.filter((rule) => rule.status === 'active').length;

  const handleDelete = (ruleId: number) => {
    if (confirm('Tem certeza que deseja remover esta regra?')) {
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = `/${accountCode}/admin/loyalty/rules/${ruleId}`;

      const methodInput = document.createElement('input');
      methodInput.type = 'hidden';
      methodInput.name = '_method';
      methodInput.value = 'delete';
      form.appendChild(methodInput);

      const csrfToken = document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute('content');
      if (csrfToken) {
        const csrfInput = document.createElement('input');
        csrfInput.type = 'hidden';
        csrfInput.name = 'authenticity_token';
        csrfInput.value = csrfToken;
        form.appendChild(csrfInput);
      }

      document.body.appendChild(form);
      form.submit();
    }
  };

  return (
    <article className="loyalty-card">
      <header className="loyalty-card__header">
        <div>
          <h2 className="loyalty-card__title">
            <ChartBar size={20} />
            Regras de Fidelidade
          </h2>
          <p className="loyalty-card__description">
            {rules.length > 0
              ? `${activeRules} de ${rules.length} regras estão ativas.`
              : 'Crie regras para automatizar pontos, badges e acessos.'}
          </p>
        </div>
        <a
          href={paths.newRule}
          className="loyalty-button loyalty-button--primary loyalty-button--compact"
        >
          <Plus size={16} />
          Nova Regra
        </a>
      </header>
      <div className="loyalty-card__body loyalty-card__body--flush">
        <div className="loyalty-table-wrap">
          <table className="loyalty-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Status</th>
                <th>Tipo Recompensa</th>
                <th className="loyalty-table__actions">Ações</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((rule) => (
                <tr key={rule.id}>
                  <td>
                    <div className="loyalty-table__title">{rule.name}</div>
                    {rule.description && (
                      <div className="loyalty-table__description">
                        {rule.description}
                      </div>
                    )}
                  </td>
                  <td>{getStatusBadge(rule.status)}</td>
                  <td>{getRewardBadge(rule.reward)}</td>
                  <td className="loyalty-table__actions">
                    <div className="loyalty-row-actions">
                      <a
                        href={`/${accountCode}/admin/loyalty/rules/${rule.id}/edit`}
                        className="loyalty-icon-button"
                        aria-label={`Editar regra ${rule.name}`}
                        title="Editar regra"
                      >
                        <Edit size={16} />
                      </a>
                      <button
                        type="button"
                        className="loyalty-icon-button loyalty-icon-button--danger"
                        onClick={() => handleDelete(rule.id)}
                        aria-label={`Remover regra ${rule.name}`}
                        title="Remover regra"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {rules.length === 0 && (
                <tr>
                  <td colSpan={4}>
                    <div className="loyalty-empty-state">
                      <div className="loyalty-empty-state__icon">
                        <FileText size={22} />
                      </div>
                      <p className="loyalty-empty-state__title">
                        Nenhuma regra cadastrada
                      </p>
                      <p className="loyalty-muted">
                        Comece com uma regra simples, como conceder pontos por
                        check-in validado.
                      </p>
                      <a
                        href={paths.newRule}
                        className="loyalty-button loyalty-button--primary loyalty-button--compact"
                      >
                        <Plus size={16} />
                        Criar primeira regra
                      </a>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </article>
  );
}
